import { FC, useState } from "react";
import { motion } from "framer-motion";
import SideBar from "../components/dashboard/SideBar";
import DashboardHeader from "../components/dashboard/header/DashboardHeader";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import { activeSideBarSelector, dashboardSelector } from "@/store/features/dashboardSlice";
import BreadcrumbComponent from "@/components/dashboard/Breadcrumb";
import { GuardedLink } from "@/common/custom/GuardedLink";
import { ChevronDown, Menu } from "lucide-react";
import Button from "@/common/ui/Button";

interface Props {
  className?: string;
  children: React.ReactNode;
}

const selectSideBarData = createSelector(
  (state: RootState) => state.dashboard.sideBars,
  (state: RootState) => state.dashboard.activeSideBarKey,
  (sideBars, activeSideBarKey) => ({ sideBars, activeSideBarKey })
);

const DashboardLayout: FC<Props> = ({ children, className = "" }) => {
  const { sideBars, activeSideBarKey } = useSelector(selectSideBarData);
  const activeSideBar = useSelector(activeSideBarSelector);
  const { routeSegments, isSideBarExpanded } = useSelector(dashboardSelector);
  const currentPath = `/${routeSegments.join("/")}`;
  const [isMobileSubMenuOpen, setIsMobileSubMenuOpen] = useState(false);

  const hideSideBar = activeSideBar?.content?.sidebars;
  const showBreadCrumb = !activeSideBar?.hideBreadCrumb;
  const isSideBarHidden = activeSideBar?.skipContentSideBar?.includes(currentPath);
  // console.log({ activeSideBar, activeSideBarKey });

  return (
    <motion.div className={`w-full min-h-screen md:h-screen flex overflow-hidden ${className}`}>
      <SideBar sideBars={sideBars} activeSideBarKey={activeSideBarKey} />
      <div className={`flex pt-12 md:pt-0 flex-col  ${isSideBarExpanded ? "md:w-[calc(100%-200px)]" : "w-[calc(100%-2px)] md:w-[calc(100%-48px)]"}`}>
        <DashboardHeader />
        {showBreadCrumb && <BreadcrumbComponent />}
        <div
          className={`${
            showBreadCrumb ? "w-[100%-100px] p-2 md:p-4" : isSideBarHidden ? "w-[calc(100%-200px)]" : "w-screen md:w-full"
          }  bg-white  flex-1 flex h-[calc(100%-112px)] shadow-lg rounded-llg`}
        >
          {activeSideBar?.content?.sidebars && hideSideBar && !isSideBarHidden && (
            <>
              <div className="hidden md:block h-full bg-containerBg w-[216px] rounded-llg">
                <ul className="p-3 text-text-default">
                  {activeSideBar?.content?.sidebars
                    .filter((c) => !c.hideContentSideBar)
                    .map((item) => {
                      const isActive = item.key == currentPath;
                      return (
                        <GuardedLink to={item?.key} key={item.key}>
                          <li
                            className={`px-3 py-2.5 text-13 rounded-md transition-all duration-100 ${
                              isActive ? "text-text-primary bg-primaryOpacity-10 font-bold " : " text-text-default"
                            } hover:text-text-primary hover:bg-primaryOpacity-10`}
                          >
                            {item.name}
                          </li>
                        </GuardedLink>
                      );
                    })}
                </ul>
              </div>
              {/* for mobile view */}
              <div className={`md:hidden absolute top-14 right-2 ${isMobileSubMenuOpen ? "z-50" : "z-20"}`}>
                {/* Mobile submenu toggle button */}
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setIsMobileSubMenuOpen(!isMobileSubMenuOpen)}
                  className="rounded-50 text-text-primary border-text-primary !px-3 !py-1 flex items-center gap-1"
                >
                  <Menu className="h-4 w-4" />
                  <ChevronDown className="h-4 w-4" />
                </Button>

                {/* Mobile submenu overlay */}
                {isMobileSubMenuOpen && <div className="fixed inset-0 z-50  bg-black bg-opacity-50" onClick={() => setIsMobileSubMenuOpen(false)} />}

                {/* Mobile submenu */}
                {isMobileSubMenuOpen && (
                  <div className="fixed top-24 right-2 z-50  p-2 bg-containerBg rounded-lg shadow-lg border border-gray-200 flex items-center gap-2">
                    <div className="bg-white rounded-lg shadow-lg min-w-64 max-h-96 overflow-y-auto">
                      <ul className="p-3 text-text-default max-h-60 overflow-y-auto">
                        {activeSideBar?.content?.sidebars
                          .filter((c) => !c.hideContentSideBar)
                          .map((item) => {
                            const isActive = item.key == currentPath;

                            return (
                              <GuardedLink to={item?.key} key={item.key}>
                                <li
                                  className={`px-3 py-2.5 text-13 rounded-md transition-all duration-100 ${
                                    isActive ? "text-text-primary bg-primaryOpacity-10 font-bold " : " text-text-default"
                                  } hover:text-text-primary hover:bg-primaryOpacity-10`}
                                  onClick={() => setIsMobileSubMenuOpen(false)}
                                >
                                  {item.name}
                                </li>
                              </GuardedLink>
                            );
                          })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          <main className="flex-1 overflow-auto overflow-x-hidden bg-white w-full">{children}</main>
        </div>
      </div>
    </motion.div>
  );
};

export default DashboardLayout;
